import type { ComponentProps, FC } from 'react';
import { css } from '@compiled/react';
import React, { Fragment, useCallback, useContext, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { parse } from 'url';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import UFOLabel from '@atlaskit/react-ufo/label';
import type { RendererAppearance } from '@atlaskit/renderer';

import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';
import { useReactProps_DO_NOT_USE } from '@atlassian/embedded-confluence/useReactProps';

import { isMobileApp_DO_NOT_USE } from '@confluence/mobile-app-detection-common';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';
import { AIFloatingContextMenuLoadable as AIFloatingContextMenu } from '@confluence/ai-floating-context-menu/entry-points/AIFloatingContextMenuLoadable.ts';
import { AnnotationsProvider } from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { DocumentUpdateStatusProvider } from '@confluence/annotation-provider-store/entry-points/DocumentUpdateStatusContext.ts';
import { ArchivedPageBannerAfterPaint } from '@confluence/archive-pages/entry-points/ArchivedPageBannerV2.ts';
import { getAMNReferralSourceFromQueryParam } from '@confluence/automated-meeting-notes/entry-points/getAMNReferralSourceFromQueryParam.ts';
import { useAutomatedMeetingNotesPageProperties } from '@confluence/automated-meeting-notes/entry-points/useAutomatedMeetingNotesPageProperties.ts';
import { ChoreographerAssetsContextProvider } from '@confluence/choreographer-services/entry-points/ChoreographerAssetsContext.ts';
import { ObjectCommentProvider } from '@confluence/comment-context/entry-points/ObjectCommentContext.ts';
import { NewCommentsProvider } from '@confluence/comment-context/entry-points/NewCommentsContext.ts';
import { PageCommentsCountProvider } from '@confluence/comment-context/entry-points/PageCommentsCountContext.ts';
import { useInlineCommentsDispatchContext } from '@confluence/comment-context/entry-points/InlineCommentsContext.ts';
import { ReactionsProvider } from '@confluence/comment-context/entry-points/ReactionsContext.ts';
import { useCommentsDataActions } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { CommentsDataBackgroundLoader } from '@confluence/comments-data-manager/entry-points/CommentsDataBackgroundLoader.tsx';
import { useProcessedCommentsDataActions } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { useCommentsPanelActions } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { PageCommentsPubSubHandler } from '@confluence/comments-pubsub/entry-points/pageComments.ts';
import { ViewCommentShortcutListener } from '@confluence/comments-shortcuts/entry-points/ViewCommentShortcutListener.ts';
import { ContentViewedEvent } from '@confluence/content-body/entry-points/ContentViewedEvent.ts';
import { ContentRefetchProvider } from '@confluence/content-body/entry-points/ContentRefetchContext.ts';
import { ContentOwnershipContextProvider } from '@confluence/content-ownership/entry-points/ContentOwnershipContext.ts';
import { ReadingAidsAcronymsHighlighter } from '@confluence/contextual-reading-aids/entry-points/readingAidsAcronymsHighlighter.ts';
import {
	CUSTOM_HEADER_FOOTER_TYPES,
	CustomHeaderFooterLoader,
	CustomFooterLoader,
} from '@confluence/custom-header-footer';
import { AutoConversionByLineContextProvider } from '@confluence/editor-conversion/entry-points/AutoConversionByLineContext.ts';
import { getAutoConvertionInfo } from '@confluence/editor-conversion/entry-points/getAutoConvertionInfo.ts';
import {
	LIVE_PAGE_PUBLISH_EXPERIENCE,
	VIEW_PAGE_EXPERIENCE,
	LIVE_PAGE_SESSION_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { getPreviousRouteExperienceData } from '@confluence/experience-tracker/entry-points/getExperienceTracker.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useOmnibarEligibility } from '@confluence/experiment-omnibar/entry-points/useOmnibarEligibility.ts';
import { useOmniBarVisibilityActions } from '@confluence/omni-bar-api/entry-points/OmniBarVisibilityStore.ts';
import {
	ImportFeedbackBanner,
	getImportDate,
	getImportSource,
	isImportBannerEnabled,
} from '@confluence/import-feedback';
import { InlineCommentsHighlighterLoader } from '@confluence/inline-comments/entry-points/InlineCommentsHighlighterLoader.ts';
import { useSetContentLastModifiedTimestamp } from '@confluence/issue-create-side-panel/entry-points/BulkCreateContextProvider.ts';
import { ViewPageLabelsLoader } from '@confluence/labels/entry-points/ViewPageLabelsLoader.ts';
import { LivePageComponentLoader as LivePageComponentClient } from '@confluence/live-pages/entry-points/LivePageComponent.ts';
import { LivePageComponentServer } from '@confluence/live-pages/entry-points/LivePageComponentServer.ts';
import { ConvertToLivePageFlag } from '@confluence/live-pages-features/entry-points/ConvertToLivePageFlag.tsx';
import { useEditorContentModeValue } from '@confluence/editor-content-mode-button/entry-points/useEditorContentModeValue.ts';
import { MovePageSuccessFlag } from '@confluence/page-tree/entry-points/MovePageSuccessFlag.tsx';
import { LIVE_PAGE_SUBTYPE } from '@confluence/live-pages-utils';
import type { CachedPageData } from '@confluence/live-pages-utils/entry-points/getCachedPageData.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useIsLivePagesFeatureEnabled } from '@confluence/live-pages-utils/entry-points/useIsLivePagesFeatureEnabled.ts';
import { useLivePageStoreActions } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { LoadingPriority } from '@confluence/loadable';
import { useMentionsNotificationsActions } from '@confluence/mentions-notifications/entry-points/useMentionsNotifications.ts';
import { LocalActivityViewPageTracker } from '@confluence/local-activity/entry-points/LocalActivityViewPageTracker.ts';
import { PDF_EXPORT_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import { NativeCollabPresenceStateContainer } from '@confluence/native-collab/entry-points/useNativeCollabPresenceState.ts';
import { StickyDraftCommentContainer } from '@confluence/object-comments/entry-points/StickyDraftCommentContainer.ts';
import { ObjectCommentWrapper } from '@confluence/object-comments/entry-points/ObjectCommentWrapper.ts';
import { ObjectSidebarControl } from '@confluence/object-sidebar-components/entry-points/ObjectSidebarControl.tsx';
import { RendererPageCoverPicture } from '@confluence/page-cover-picture/entry-points/RendererPageCoverPicture.ts';
import { getRendererCoverPictureInfo } from '@confluence/page-cover-picture/entry-points/coverPictureHelperFunctions.ts';
import { EndOfPageRecommendationFinalLoader } from '@confluence/page-recommendations/entry-points/EndOfPageRecommendationLoader.ts';
import { QuickReload } from '@confluence/quick-reload/entry-points/QuickReload.ts';
import { PageMode } from '@confluence/quick-reload/entry-points/pubSubConstants.ts';
import { RendererActionsProvider } from '@confluence/renderer-actions/entry-points/RendererActionsContext.ts';
import { ScrollToPageStart } from '@confluence/scroll/entry-points/ScrollToPageStart.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';
import { setIsLivePage } from '@confluence/unique-page-load-id/entry-points/UniquePageLoadId.ts';
import {
	ATL_GENERAL,
	ATL_FOOTER,
} from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { useConnectAppsData } from '@confluence/web-panel-location/entry-points/useConnectAppsData.ts';
import { NthUserOnboardingBannerComponent } from '@confluence/experiment-nth-user-getting-started-page/entry-points/NthUserOnboardingBannerComponent.ts';
import { useNthUserOnboardingPageTracking } from '@confluence/experiment-nth-user-getting-started-page/entry-points/useNthUserOnboardingPageTracking.ts';
import { useAmplitudeSessionReplay } from '@confluence/amplitude-session-replay/entry-points/AmplitudeSessionReplay.ts';

import type { BaseViewPageProps } from './BaseViewPageProps.ts';
import { ContentTitleWithByLine } from './ContentTitleWithByLine.tsx';
import { Divider } from './Divider.tsx';
import { SatisfactionSurveysLoader } from './SatisfactionSurveysLoader.ts';
import { ViewPageContainer } from './ViewPageContainer.tsx';
import type { ViewPageContentLoader } from './ViewPageContentLoader.tsx';
import { ViewPageFooterComments } from './ViewPageFooterComments.tsx';
import { ViewPageInlineComments } from './ViewPageInlineComments.tsx';
import { ViewPageReactions } from './ViewPageReactions.tsx';
import { useRenderLivePageEarly } from './useRenderLivePageEarly.tsx';
import { ViewPageContent } from './ViewPageContent.tsx';
import { ViewPageMainContentContainer } from './ViewPageMainContentContainer.tsx';
import { ViewPageSplitTinyUFO } from './ViewPageSplitTinyUFO.tsx';

// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
const LivePageComponent = process.env.REACT_SSR ? LivePageComponentServer : LivePageComponentClient;

export const setLoadingPriority = (hasAllowedFeature: boolean) => {
	if (isEmbeddedConfluence_DO_NOT_USE() || isMobileApp_DO_NOT_USE()) {
		return hasAllowedFeature ? LoadingPriority.PAINT : LoadingPriority.LAZY;
	}
	return null;
};

type ContentTitleAndByLineOptions = Pick<
	ComponentProps<typeof ContentTitleWithByLine>,
	'hasByLineContributors' | 'hasByLineExtensions' | 'headingContainer'
>;

type Props = BaseViewPageProps & {
	contentHeader?: React.ReactNode;
	hasByLine?: boolean;
	hasPageComments?: boolean;
	hasSatisfactionSurveys?: boolean;
	hasInlineComments?: boolean;
	hasProgressiveRendering?: boolean;
	hasLabels?: boolean;
	hasReactions?: boolean;
	hasEOPRecs?: boolean;
	hasQuickReload?: boolean;
	appearance?: string;
	classicComments?: React.ComponentType<any>;
	hasInlineActions?: boolean;
	contentScreenStyles?: ComponentProps<typeof ViewPageContainer>['contentScreenStyles'];
	eventHandlers?: React.ComponentProps<typeof ViewPageContentLoader>['eventHandlerOverrides'];
	titleAndByLineOptions?: ContentTitleAndByLineOptions;
	children?: React.ReactNode;
	setHasCoverPicture?: (hasCoverPicture: boolean) => void;
	isEmbeddedPage?: boolean;
	ClassicEditorContextProviderLoader?: React.ComponentType<any>;
	renderLivePageAsViewPage?: boolean;
	cachedPageData?: CachedPageData;
	disableTableOverflowShadow?: boolean;
};

const hideDuringPdfPrint = css({
	'@media print': {
		display: 'none',
	},
});

// NOTE: `contentId` represents the user's intended page but not always the displayed one. During transition to a new page, the component still renders the previous page until ViewPageContainer updates the contentQueryData.
//       While fetching new contentQueryData, the prior contentQueryData is displayed, causing a brief mismatch between contentId and the contentQueryData's contentId.
export const ViewPageCommon: FC<Props> = ({
	spaceKey,
	contentId,
	contentHeader,
	contentScreenStyles,
	classicComments,
	eventHandlers,
	titleAndByLineOptions,
	hasByLine = true,
	hasPageComments = true,
	hasSatisfactionSurveys = true,
	hasInlineComments = true,
	hasInlineActions = true,
	hasLabels = true,
	hasReactions = true,
	hasEOPRecs = true,
	hasQuickReload = true,
	hasProgressiveRendering,
	appearance,
	children,
	setHasCoverPicture,
	isEmbeddedPage,
	ClassicEditorContextProviderLoader,
	renderLivePageAsViewPage = false,
	cachedPageData = {},
}) => {
	const { locale } = useSessionData();
	const setContentLastModifiedTimestamp = useSetContentLastModifiedTimestamp();
	const { isLivePagesFeatureEnabled, livePageReleasePhase } = useIsLivePagesFeatureEnabled();
	const { isLivePage: isLivePageOnSSR } = getSSRRenderInputs();
	const { resetInitialDataLoadedMap } = useCommentsPanelActions();
	const { clearCommentsData } = useCommentsDataActions();
	const { clearProcessedData } = useProcessedCommentsDataActions();
	const { clearMentionsFlags } = useMentionsNotificationsActions();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { isAIEnabledForContent, isRovoEnabledForContent } = useIsAIEnabledForContent({
		contentId,
	});
	let { embeddedConfluenceSource } = useReactProps_DO_NOT_USE() || {};
	const { mode: contentMode } = useEditorContentModeValue({ contentId });
	if (!embeddedConfluenceSource && isEmbeddedPage) {
		embeddedConfluenceSource = parse(
			location.search,
			true,
		).query.embeddedConfluenceSource?.toString();
	}

	const isPdfExport = PDF_EXPORT_PAGE.match(window.location.pathname);

	const isReadingAidsAutoHighlightEnabled =
		(isAIEnabledForContent || isRovoEnabledForContent) && locale.startsWith('en');

	// Setup for A/A experiment for testing stratified sampling with Statsig
	expVal('cc_stratified_sampling_exp_aa', 'cohort', 'not-controlled');

	let stickyHeader = contentHeader;

	const isLivePage = useIsCurrentPageLive();
	const { setIsLive } = useLivePageStoreActions();
	const { showReactions: showOmnibarReactions } = useOmnibarEligibility(
		isLivePage || !!isPdfExport,
	);
	const [pageCommentsSectionRef, pageCommentsSectionInView] = useInView();
	const [endOfPageRecRef, endOfPageRecInView] = useInView();
	const { requestHideOmniBar, releaseHideOmniBar } = useOmniBarVisibilityActions();

	useEffect(() => {
		if (endOfPageRecInView) {
			requestHideOmniBar();
			return () => releaseHideOmniBar();
		}
	}, [endOfPageRecInView, requestHideOmniBar, releaseHideOmniBar]);
	const { setActiveHighlight } = useInlineCommentsDispatchContext();

	const shouldShowBylineReactions =
		hasReactions && !isLivePage && fg('cc_page_experiences_new_editor_byline');

	const setLivePageGlobalState = useCallback(
		(isContentLive: boolean) => {
			void setIsLive(isContentLive);
			void setIsLivePage(isContentLive); // page load state used for analytics context (e.g. for experience tracking)
		},
		[setIsLive],
	);

	useAmplitudeSessionReplay({
		skip: !expVal('cc_engagement_amplitude_session_replay_exp', 'isEnabled', false),
	});

	useNthUserOnboardingPageTracking({
		contentId,
		skip: !fg('cc-engagement-getting-started-nth-user-gate'),
	});

	useEffect(() => {
		if (!isLivePage) return; // no cleanup needed

		// This is the cleanup function that runs when the component unmounts
		return () => {
			setLivePageGlobalState(false);
		};
	}, [isLivePage, setLivePageGlobalState]);

	// Update view-page experience with `isLivePage` && `livePageReleasePhase` attributes to ensure all future experience fails denote whether it was on a live page
	useEffect(() => {
		const experienceState = experienceTracker.getExperienceState(VIEW_PAGE_EXPERIENCE);

		if (
			experienceState?.attributes?.contentId === contentId &&
			experienceState?.attributes?.isLivePage !== isLivePage
		) {
			experienceTracker.updateAttributes({
				name: VIEW_PAGE_EXPERIENCE,
				attributes: { isLivePage, livePageReleasePhase },
			});
		}
	}, [contentId, isLivePage, livePageReleasePhase, experienceTracker]);

	// stickyHeader needs to be removed based on live page state since it's used in ViewPageContainer props
	if (isLivePage) {
		stickyHeader = undefined;
	}
	const {
		renderLivePageEarly,
		isLivePageCacheOutdated,
		isFastLoadedLivePageArchived,
		isLivePageFastLoadedFromSSR,
	} = useRenderLivePageEarly({
		contentId,
		cachedSubType: cachedPageData.subType,
		cachedIsArchived: cachedPageData.isArchived,
		renderLivePageAsViewPage,
	});

	useEffect(() => {
		if (renderLivePageEarly) {
			setLivePageGlobalState(true);
		}
	}, [renderLivePageEarly, setLivePageGlobalState]);

	useEffect(() => {
		return () => {
			resetInitialDataLoadedMap();
			clearCommentsData();
			if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
				clearProcessedData();
			}
			clearMentionsFlags();
			setActiveHighlight(null);
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentId]);

	let livePageFastLoadedSource = '';
	if (renderLivePageEarly) {
		if (isLivePageFastLoadedFromSSR) {
			livePageFastLoadedSource = 'SSR';
		} else if (cachedPageData?.gqlType) {
			livePageFastLoadedSource = `GQL:${cachedPageData.gqlType}`;
		} else {
			livePageFastLoadedSource = 'Unknown';
		}
	}

	const { successfulExperiences: previousRouteSuccessfulExperiences } =
		getPreviousRouteExperienceData();
	const livePagePerfData = useMemo(
		() => ({
			renderLivePageEarly,
			isLivePageCacheOutdated,
			isFastLoadedLivePageArchived,
			isLivePageFastLoadedFromSSR,
			livePageFastLoadedSource,
			contentExtensionMetrics: {},
			previousRouteSuccessfulExperiences: Array.from(previousRouteSuccessfulExperiences),
		}),
		[
			renderLivePageEarly,
			isLivePageCacheOutdated,
			isFastLoadedLivePageArchived,
			isLivePageFastLoadedFromSSR,
			livePageFastLoadedSource,
			previousRouteSuccessfulExperiences,
		],
	);

	const panelsModuleKeysAtlGeneral = useConnectAppsData({
		contentId,
		location: ATL_GENERAL,
	});
	const panelsModuleKeysAtlFooter = useConnectAppsData({
		contentId,
		location: ATL_FOOTER,
	});

	const customPerfData = {
		...livePagePerfData,
		webPanelsAtlGeneral: panelsModuleKeysAtlGeneral,
		webPanelsAtlFooter: panelsModuleKeysAtlFooter,
		...(embeddedConfluenceSource ? { embeddedConfluenceSource } : {}),
	};

	if (fg('editor-ai-contentmode-performance-metric')) {
		customPerfData['isContentModeDense'] = contentMode === 'compact';
	}

	const {
		isAMN,
		isLoading: isAMNPagePropertiesLoading,
		isRecurringParent,
		isLiveAMNPage,
	} = useAutomatedMeetingNotesPageProperties(contentId);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		// maintain analytics for AMN page views without triggering spotlight
		if (!isAMNPagePropertiesLoading && isAMN) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'viewAMNPage',
					attributes: {
						isLivePage: isLiveAMNPage,
						isRecurringParent,
						referralSource: getAMNReferralSourceFromQueryParam(),
					},
				},
			}).fire();
		}
	}, [
		contentId,
		isAMN,
		isRecurringParent,
		isLiveAMNPage,
		isAMNPagePropertiesLoading,
		createAnalyticsEvent,
	]);

	return (
		<Fragment>
			<ContentOwnershipContextProvider>
				<ObjectCommentProvider>
					<NewCommentsProvider>
						<PageCommentsCountProvider contentId={contentId}>
							<DocumentUpdateStatusProvider>
								<ContentRefetchProvider>
									<RendererActionsProvider>
										<AnnotationsProvider>
											<ChoreographerAssetsContextProvider>
												{renderLivePageEarly ? (
													<NativeCollabPresenceStateContainer scope={contentId}>
														<LivePageComponent
															contentId={contentId}
															spaceKey={spaceKey}
															ClassicEditorContextProviderLoader={
																ClassicEditorContextProviderLoader
															}
															customPerfData={customPerfData}
															loadingPriority={setLoadingPriority(true)}
															isFastLoaded
															hasByLineContributors={
																titleAndByLineOptions?.hasByLineContributors ?? true
															}
															hasByLineExtensions={
																titleAndByLineOptions?.hasByLineExtensions ?? true
															}
															hasReactions={hasReactions}
															hasPageComments={hasPageComments}
															hasInlineComments={hasInlineComments}
														/>
													</NativeCollabPresenceStateContainer>
												) : (
													<Fragment>
														<ViewPageContainer
															spaceKey={spaceKey}
															contentId={contentId}
															stickyHeader={stickyHeader}
															contentScreenStyles={contentScreenStyles}
															customPerfData={customPerfData}
														>
															{({ isThemed, contentQueryData, contentQueryError }) => {
																const contentStatus = contentQueryData?.content?.nodes?.[0]?.status;
																const contentType = contentQueryData?.content?.nodes?.[0]?.type;
																const contentSubType =
																	contentQueryData?.content?.nodes?.[0]?.subType;
																const contentTitle = contentQueryData?.content?.nodes?.[0]?.title;
																const spaceId = contentQueryData?.content?.nodes?.[0]?.space?.id;
																const contentIdFromQuery =
																	contentQueryData?.content?.nodes?.[0]?.id;
																const lastModifiedDate =
																	contentQueryData?.content?.nodes?.[0]?.metadata?.lastModifiedDate;
																void setContentLastModifiedTimestamp(lastModifiedDate);
																const pageWidthType =
																	contentQueryData?.content?.nodes?.[0]?.appearancePublished
																		?.nodes?.[0]?.value || `"default"`;
																const isFabricPage = Boolean(
																	contentQueryData?.content?.nodes?.[0]?.body?.dynamic
																		?.representation === 'atlas_doc_format',
																);
																const hasLegacyContent =
																	contentQueryData?.content?.nodes?.[0]?.body?.dynamic?.value?.includes(
																		'"extensionKey":"legacy-content"',
																	);
																const showAIButton =
																	(contentType === 'page' || contentType === 'blogpost') &&
																	!isEmbeddedPage;
																const showAIButtonForTinyMCE =
																	!isFabricPage && fg('confluence_frontend_content_wrapper');

																// getting info cover picture needs
																const {
																	coverPicture,
																	contentAppearance,
																	hasCoverPicture,
																	coverPictureWidth,
																} = getRendererCoverPictureInfo(contentQueryData, false);
																// getting info for auto conversion
																const {
																	queryDataId,
																	editorProperty,
																	isAutoConverted,
																	fabricEditorSupported,
																} = getAutoConvertionInfo(contentQueryData?.content?.nodes?.[0]);

																// If live page is archived, we don't show LivePageComponent. Instead, archived live pages will render using the existing ViewPageContentRenderer.
																const isContentLive =
																	isLivePageOnSSR ||
																	(contentQueryData?.content?.nodes?.[0]?.subType ===
																		LIVE_PAGE_SUBTYPE &&
																		contentStatus !== 'archived');

																if (isLivePagesFeatureEnabled) {
																	setLivePageGlobalState(isContentLive);

																	if (
																		isContentLive &&
																		!contentQueryError &&
																		!!contentIdFromQuery &&
																		!renderLivePageAsViewPage
																	) {
																		return (
																			<NativeCollabPresenceStateContainer scope={contentId}>
																				<LivePageComponent
																					contentId={contentIdFromQuery}
																					spaceKey={spaceKey}
																					ClassicEditorContextProviderLoader={
																						ClassicEditorContextProviderLoader
																					}
																					customPerfData={customPerfData}
																					isFastLoaded={false}
																					hasByLineContributors={
																						titleAndByLineOptions?.hasByLineContributors ?? true
																					}
																					hasByLineExtensions={
																						titleAndByLineOptions?.hasByLineExtensions ?? true
																					}
																					hasReactions={hasReactions}
																					hasPageComments={hasPageComments}
																					hasInlineComments={hasInlineComments}
																				/>
																			</NativeCollabPresenceStateContainer>
																		);
																	}
																}

																if (!!setHasCoverPicture) {
																	setHasCoverPicture(hasCoverPicture);
																}

																const livePagePublishExperienceState =
																	experienceTracker.getExperienceState(
																		LIVE_PAGE_PUBLISH_EXPERIENCE,
																	);
																if (
																	livePagePublishExperienceState &&
																	!livePagePublishExperienceState.hasStopped &&
																	livePagePublishExperienceState.attributes?.contentId === contentId
																) {
																	// Succeed the live-page/publish and live-page-session experiences on render of view page content after conversion to page
																	experienceTracker.succeed({
																		name: LIVE_PAGE_PUBLISH_EXPERIENCE,
																	});

																	experienceTracker.succeed({
																		name: LIVE_PAGE_SESSION_EXPERIENCE,
																		attributes: {
																			convertedToPage: true,
																		},
																	});
																}

																const importedContentSource = getImportSource(contentQueryData);
																const importedContentDate = getImportDate(contentQueryData);
																const isImportBannerShown =
																	isImportBannerEnabled(contentQueryData) &&
																	importedContentSource &&
																	importedContentDate;

																const maybeScrollToPageStart =
																	Boolean(window.__SSR_RENDERED__) &&
																	expVal(
																		'confluence_p2m_style_recalc_and_expand_joint_exp',
																		'isEnabled',
																		false,
																	) ? null : (
																		<ScrollToPageStart
																			key={`ScrollToPageStart-${contentId}`}
																			isSmooth={false}
																		/>
																	);

																return (
																	<Fragment>
																		{maybeScrollToPageStart}
																		<ViewPageMainContentContainer isFabricPage={isFabricPage}>
																			{/* ViewPageMainContentContainer should contain all page content above footer comments */}
																			{isThemed && contentHeader}
																			<ViewPageSplitTinyUFO isFabricPage={isFabricPage} />
																			<CustomHeaderFooterLoader
																				type={CUSTOM_HEADER_FOOTER_TYPES.HEADER}
																			/>
																			{isImportBannerShown && (
																				<ImportFeedbackBanner
																					importSource={importedContentSource}
																					importDate={importedContentDate}
																					contentId={contentId}
																				/>
																			)}
																			{hasCoverPicture && (
																				<RendererPageCoverPicture
																					contentId={contentId}
																					isPagePreview={false}
																					coverPicture={coverPicture}
																					contentAppearance={contentAppearance}
																					isSpaceOverview={false}
																					coverPictureWidthOverride={coverPictureWidth}
																				/>
																			)}
																			<AutoConversionByLineContextProvider
																				editorProperty={editorProperty}
																				fabricEditorSupported={fabricEditorSupported}
																				isAutoConverted={!!isAutoConverted}
																				queryDataId={queryDataId}
																			>
																				<ReactionsProvider contentId={contentId}>
																					<ContentTitleWithByLine
																						banner={
																							<ArchivedPageBannerAfterPaint contentId={contentId} />
																						}
																						contentId={contentId}
																						spaceKey={spaceKey || ''}
																						hasCoverPicture={hasCoverPicture}
																						pageWidthType={pageWidthType}
																						isEmbeddedPage={isEmbeddedPage}
																						shouldShowBylineReactions={shouldShowBylineReactions}
																						contentType={contentType}
																						contentSubType={contentSubType}
																						spaceId={spaceId}
																						{...titleAndByLineOptions}
																						hasByLine={hasByLine}
																					/>
																				</ReactionsProvider>
																			</AutoConversionByLineContextProvider>
																			{/* Body of content */}
																			<ViewPageContent
																				hasInlineComments={hasInlineComments}
																				hasProgressiveRendering={hasProgressiveRendering}
																				contentId={contentId}
																				data={contentQueryData}
																				error={contentQueryError}
																				spaceKey={spaceKey}
																				eventHandlerOverrides={eventHandlers}
																				appearance={appearance as RendererAppearance}
																				hasInlineActions={hasInlineActions}
																				lastModifiedDate={lastModifiedDate || undefined}
																				isEmbeddedPage={isEmbeddedPage}
																				customPerfData={customPerfData}
																				disableTableOverflowShadow
																			/>
																			{fg('cc-engagement-getting-started-nth-user-gate') && (
																				<NthUserOnboardingBannerComponent
																					contentID={contentId}
																					spaceKey={spaceKey}
																				/>
																			)}
																			{/* https://product-fabric.atlassian.net/browse/COMMENTS-5436 */}
																			{hasInlineComments &&
																				(!isFabricPage ||
																					(hasLegacyContent &&
																						!fg('confluence_comment_disable_legacy_comment'))) && (
																					<InlineCommentsHighlighterLoader pageId={contentId} />
																				)}
																			{isReadingAidsAutoHighlightEnabled && (
																				<ReadingAidsAcronymsHighlighter contentId={contentId} />
																			)}
																			{hasLabels &&
																				(isFabricPage &&
																				fg('confluence_frontend_labels_in_footer') ? null : (
																					<ViewPageLabelsLoader
																						pageWidthType={pageWidthType}
																						contentId={contentId}
																						contentType={contentType}
																						spaceKey={spaceKey}
																						spaceId={spaceId}
																						contentSubType={contentSubType}
																					/>
																				))}
																			{/* Render all secondary content - likes, labels, comments, etc. */}
																			{hasEOPRecs &&
																				(contentType == 'page' || contentType == 'blogpost') && (
																					<div ref={endOfPageRecRef}>
																						<EndOfPageRecommendationFinalLoader
																							entityType={contentType || ''}
																							entityId={contentId}
																							spaceKey={spaceKey || ''}
																							pageWidthType={pageWidthType}
																						/>
																					</div>
																				)}
																			{/* eslint-disable-next-line check-react-ssr-usage/no-react-ssr */}
																			{!process.env.REACT_SSR && (
																				<Fragment>
																					<ContentViewedEvent contentId={contentId} />
																					{expVal(
																						'cc_comments_new_comments_data_manager',
																						'isEnabled',
																						false,
																					) && (
																						<CommentsDataBackgroundLoader contentId={contentId} />
																					)}
																				</Fragment>
																			)}
																			{isFabricPage && (
																				<div
																					css={[
																						expValEquals(
																							'afm-hide-elements-from-pdf-print',
																							'isEnabled',
																							true,
																						) && hideDuringPdfPrint,
																					]}
																				>
																					<ObjectSidebarControl
																						hasCommentsButton
																						hasDetailsButton
																						hasLoomButton={contentStatus !== 'archived'}
																						hasAiButton={showAIButton}
																						contentId={contentId}
																						spaceKey={spaceKey}
																						contentType={contentType}
																						contentTitle={contentTitle}
																						lastModifiedDate={lastModifiedDate}
																						pageCommentsSectionInView={pageCommentsSectionInView}
																						hasAudioButton={isAIEnabledForContent}
																					/>
																				</div>
																			)}
																			{hasReactions && !fg('confluence_frontend_content_wrapper')
																				? /* Disable unsupported reactions when rendering live page as view page */ !isLivePage && (
																						<ReactionsProvider contentId={contentId}>
																							<ViewPageReactions
																								hasReactions={hasReactions}
																								contentId={contentId}
																								contentType={contentType}
																								spaceId={spaceId}
																								contentSubType={contentSubType}
																							/>
																						</ReactionsProvider>
																					)
																				: !(
																						isPdfExport || fg('confluence_frontend_content_wrapper')
																					) && ( // Hide divider if pdf export or new footer comment style (which has its own divider)
																						<Divider testId="reaction-placeholder-divider" />
																					)}
																		</ViewPageMainContentContainer>
																		{/* Omni-bar: Persistent collaboration bar at bottom of page */}
																		{!isLivePage && !isPdfExport && showOmnibarReactions}
																		{hasPageComments &&
																			/* Disable unsupported page comments when rendering live page as view page */ !isLivePage && (
																				<>
																					{fg('confluence_comment_sticky_footer_enabled') && (
																						<StickyDraftCommentContainer
																							pageWidthType={pageWidthType}
																							key={`StickyDraftCommentContainer-${contentId}`}
																						/>
																					)}
																					<UFOLabel name="renderer-footer-comments">
																						<div
																							ref={pageCommentsSectionRef}
																							data-vc="footer-comments"
																						>
																							{!fg('confluence_frontend_content_wrapper') && (
																								<ViewPageFooterComments
																									key={contentId}
																									contentId={contentId}
																									loadingPriority={
																										fg(
																											'confluence_frontend_remove_comments_placeholder',
																										)
																											? LoadingPriority.PAINT
																											: setLoadingPriority(hasPageComments)
																									}
																									classicComments={classicComments}
																									hasReactions={hasReactions}
																								/>
																							)}
																							{fg('confluence_frontend_content_wrapper') && (
																								<ObjectCommentWrapper
																									showGeneralComment
																									showLabels={Boolean(hasLabels) && isFabricPage}
																									pageWidthType={pageWidthType}
																									loadingPriority={
																										fg(
																											'confluence_frontend_ssr_empty_footer_comments',
																										)
																											? LoadingPriority.PAINT
																											: undefined
																									}
																									key={`ObjectCommentWrapper-${contentId}`} // ensures safe re-render on page change
																									hasReactions={hasReactions}
																								/>
																							)}
																						</div>
																					</UFOLabel>
																				</>
																			)}
																		<ViewCommentShortcutListener
																			isFabricPage={isFabricPage}
																			contentId={contentId}
																		/>
																		{expVal('cc_footer_loadable_fix', 'isEnabled', false) ? (
																			<CustomFooterLoader
																				type={CUSTOM_HEADER_FOOTER_TYPES.FOOTER}
																			/>
																		) : (
																			<CustomHeaderFooterLoader
																				type={CUSTOM_HEADER_FOOTER_TYPES.FOOTER}
																			/>
																		)}
																		{isFabricPage &&
																			fg('confluence_frontend_comments_panel_v2') && (
																				<PageCommentsPubSubHandler
																					contentId={contentId}
																					contentType={contentType}
																				/>
																			)}
																		{hasQuickReload && (
																			<QuickReload
																				contentId={contentId}
																				contentType={contentType}
																				// The 'Date.now()' is expedient; see notes in quick-reload
																				// package on what a more correct lastPollTime value would be.
																				lastPollTime={Date.now()}
																				pageMode={PageMode.RENDERER}
																				pageCommentsSectionInView={pageCommentsSectionInView}
																				isFabricPage={isFabricPage}
																			/>
																		)}
																		{hasSatisfactionSurveys && (
																			<SatisfactionSurveysLoader
																				contentId={contentId}
																				isEditorExpEnabled={fg('cc_onboarding_experience')}
																			/>
																		)}
																		{contentType === 'page' && (
																			<LocalActivityViewPageTracker contentId={contentId} />
																		)}
																		{children}
																		{hasInlineComments && (
																			<ViewPageInlineComments
																				key={contentId}
																				loadingPriority={setLoadingPriority(hasInlineComments)}
																				contentId={contentId}
																				isFabricPage={isFabricPage}
																			/>
																		)}
																		{isLivePagesFeatureEnabled && contentType === 'page' && (
																			<ConvertToLivePageFlag />
																		)}
																		{expVal(
																			'confluence_modernized_move_dialog',
																			'isEnabled',
																			false,
																		) && <MovePageSuccessFlag />}
																		{showAIButton && showAIButtonForTinyMCE && (
																			<AIFloatingContextMenu
																				spaceKey={spaceKey ?? ''}
																				contentId={contentId}
																				contentType={contentType}
																				lastModifiedDate={lastModifiedDate}
																				pageCommentsSectionInView={pageCommentsSectionInView}
																			/>
																		)}
																	</Fragment>
																);
															}}
														</ViewPageContainer>
													</Fragment>
												)}
											</ChoreographerAssetsContextProvider>
										</AnnotationsProvider>
									</RendererActionsProvider>
								</ContentRefetchProvider>
							</DocumentUpdateStatusProvider>
						</PageCommentsCountProvider>
					</NewCommentsProvider>
				</ObjectCommentProvider>
			</ContentOwnershipContextProvider>
		</Fragment>
	);
};
